import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { alignItems, display, flexDirection, space } from 'styled-system';

import Alert from 'common/components/Alert';
import { Grid, GridItem } from 'common/components/Grid';
import Icon from 'common/components/Icon';
import { MessageType } from 'common/enums/Alert';
import { useOpenDrawer } from 'common/hooks/drawer';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import {
  dispatchHomeClubSectionClickEvent,
  HomeClubSectionEventInteraction,
  HomeClubSectionEventPlacement,
} from 'common/tracking/home-club';
import { isLocaleWithHomeClubQRCode } from '../../utils/locale-with-qr-code';
import BirthdayCard from '../BirthdayCard/BirthdayCard';
import BaseCTAButton from '../shared/BaseCTAButton';
import FixedBottomContainer from './FixedBottomContainer';
import HomeClubMemberDrawer from './HomeClubMemberDrawer';

interface HomeClubMemberContentProps {
  homeClubId: string;
  successText: Nullable<string>;
  showBirthdayCard: boolean;
  setSuccessMessage: (text: Nullable<string>) => void;
  onBirthdayCardClose: () => void;
}

const Container = styled(GridItem)<any>`
  ${space};
  ${display};
  ${flexDirection};
  ${alignItems};
`;

const containerStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const StyledBirthdayCard = styled(BirthdayCard)<SpaceProps>`
  ${space};
`;

const StyledIcon = styled(Icon)<SpaceProps>`
  ${space};
`;

const HomeClubMemberContent = ({
  homeClubId,
  successText,
  showBirthdayCard,
  setSuccessMessage,
  onBirthdayCardClose,
}: HomeClubMemberContentProps) => {
  const { t, locale } = useLocalization();
  const ctaButtonLabel = t('h24_hc_show_qr_code_btn_label');
  const title = t('h24_hc_drawer_title');
  const showQRCodeIcon = isLocaleWithHomeClubQRCode(locale);

  const openDrawer = useOpenDrawer({
    title,
    content: <HomeClubMemberDrawer homeClubId={homeClubId} />,
  });

  const onClickHandler = () => {
    openDrawer();
    dispatchHomeClubSectionClickEvent({
      button_text: ctaButtonLabel,
      interaction: HomeClubSectionEventInteraction.ShowQrCode,
      placement: HomeClubSectionEventPlacement.Inline,
    });
  };

  const ctaButtonContent = (
    <>
      {showQRCodeIcon && <StyledIcon name="qrCode" size="24px" mr="sp_4" />}
      {ctaButtonLabel}
    </>
  );

  return (
    <>
      {!!successText && (
        <Grid>
          <Container {...containerStyles}>
            <Alert type={MessageType.Success} mb="sp_16">
              {successText}
            </Alert>
          </Container>
        </Grid>
      )}

      <Grid>
        <Container {...containerStyles} as="section" aria-label={title}>
          <BaseCTAButton display={['none', null, 'flex']} onClick={onClickHandler}>
            {ctaButtonContent}
          </BaseCTAButton>
          {showBirthdayCard && (
            <StyledBirthdayCard
              mt={['sp_0', null, 'sp_24', 'sp_32']}
              setSuccessMessage={setSuccessMessage}
              onBirthdayCardClose={onBirthdayCardClose}
            />
          )}
        </Container>
      </Grid>

      <FixedBottomContainer>
        <BaseCTAButton onClick={onClickHandler}>{ctaButtonContent}</BaseCTAButton>
      </FixedBottomContainer>
    </>
  );
};

export default HomeClubMemberContent;
